import { useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type ScreenReaderInstructions,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDroppedOrder } from '../../lib/useDroppedOrder.ts';
import type { MeetingTemplateListLabels } from './meeting-template-list-labels.ts';
import type { MeetingTemplateRow } from './meeting-template-types.ts';
import { SortableTemplateListRow } from './sortable-template-list-row.tsx';
import { TemplateListRow } from './template-list-row.tsx';

export interface TemplateListRowsProps {
  templates: MeetingTemplateRow[];
  labels: MeetingTemplateListLabels;
  /* Whether this reader may arrange this list. */
  sortable: boolean;
  canMakeDefault: boolean;
  onOpen: (templateId: string) => void;
  onMakeDefault?: (templateId: string) => void;
  /* The whole section, in the order it now stands in. */
  onReorder?: (ids: string[]) => void;
}

/**
 * One section's rows, draggable for the people who keep that section. A
 * reader gets plain rows and no drag machinery: a grip they cannot use is
 * worse than the list they came to read.
 */
export function TemplateListRows({
  templates: handedIn,
  labels,
  sortable,
  canMakeDefault,
  onOpen,
  onMakeDefault,
  onReorder,
}: TemplateListRowsProps) {
  /*
   * A few pixels of slack, or a plain click on the grip already starts a drag.
   * The pointer is the only sensor: the arrows are answered on the grip.
   */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  /* What the last keyboard move did, for a reader who cannot see it happen. */
  const [moved, setMoved] = useState('');

  const [templates, holdOrder] = useDroppedOrder(handedIn);

  if (!sortable) {
    return templates.map((template) => (
      <TemplateListRow
        key={template.id}
        template={template}
        labels={labels}
        canMakeDefault={canMakeDefault}
        onOpen={onOpen}
        onMakeDefault={onMakeDefault}
      />
    ));
  }

  /* The whole order is sent, so what is stored is the list on the screen. */
  function reorder(order: string[]) {
    holdOrder(order);
    onReorder?.(order);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (over === null || active.id === over.id) return;

    const ids = templates.map((template) => template.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;

    reorder(arrayMove(ids, from, to));
  }

  function moveBy(templateId: string, direction: -1 | 1) {
    const ids = templates.map((template) => template.id);
    const from = ids.indexOf(templateId);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= ids.length) return;

    setMoved(
      labels.dragOver({
        name: nameOf(templateId),
        position: to + 1,
        total: ids.length,
      }),
    );
    reorder(arrayMove(ids, from, to));
  }

  /* A row is its name to the person moving it, never the id it is keyed by. */
  function nameOf(id: UniqueIdentifier): string {
    return templates.find((template) => String(id) === template.id)?.name ?? '';
  }

  function placeOf(id: UniqueIdentifier): number {
    return templates.findIndex((template) => String(id) === template.id) + 1;
  }

  const screenReaderInstructions: ScreenReaderInstructions = {
    draggable: labels.dragInstructions,
  };

  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      labels.dragStart({
        name: nameOf(active.id),
        position: placeOf(active.id),
        total: templates.length,
      }),
    /* Silent over its own place, which it is the instant it is picked up. */
    onDragOver: ({ active, over }) =>
      over && over.id !== active.id
        ? labels.dragOver({
            name: nameOf(active.id),
            position: placeOf(over.id),
            total: templates.length,
          })
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? labels.dragEnd({
            name: nameOf(active.id),
            position: placeOf(over.id),
            total: templates.length,
          })
        : labels.dragCancel({ name: nameOf(active.id) }),
    onDragCancel: ({ active }) => labels.dragCancel({ name: nameOf(active.id) }),
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{ announcements, screenReaderInstructions }}
    >
      <SortableContext
        items={templates.map((template) => template.id)}
        strategy={verticalListSortingStrategy}
      >
        {templates.map((template) => (
          <SortableTemplateListRow
            key={template.id}
            template={template}
            labels={labels}
            canMakeDefault={canMakeDefault}
            onOpen={onOpen}
            onMakeDefault={onMakeDefault}
            onMove={(direction) => moveBy(template.id, direction)}
          />
        ))}
      </SortableContext>

      {/* dnd-kit never announces a keyboard move: it is over when it is made. */}
      <p aria-live='polite' className='sr-only'>
        {moved}
      </p>
    </DndContext>
  );
}
